import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Restaurant } from '@restworld/utility-types';

import {
  EmployerCard,
  EmployerCardHeader,
  EmployerCardImages,
  EmployerCardInfo,
  EmployerCardFooter,
  EmployerCandidate,
} from './employer-card';
import { EyeSlashIcon } from '@heroicons/react/24/solid';

export interface EmployerRestaurantCardProps extends Pick<Restaurant, 'address' | 'pictures' | 'types'> {
  openJpsCount?: number;
  candidates?: {
    count: number;
    candidates: EmployerCandidate[];
    message: string;
    isAllSpontaneous?: boolean;
  };
  restaurantLogo: string;
  restaurantName: string;
  href: string;
  onClick?: () => void;
  isShowcaseOffline?: boolean;
  isHighlighted?: boolean;
}

export function EmployerRestaurantCard({
  address,
  pictures,
  restaurantName,
  restaurantLogo,
  types,
  candidates,
  openJpsCount,
  href,
  onClick,
  isShowcaseOffline,
  isHighlighted,
}: EmployerRestaurantCardProps) {
  const { t: translateCommon } = useTranslation('common');
  const badgeVariant = openJpsCount ? 'success' : 'disabled';
  const bagdeText = openJpsCount
    ? translateCommon('open_positions', { count: openJpsCount })
    : translateCommon('closed_positions');

  const restaurantTypes = useMemo(() => types?.map((type) => type.type_var.label).join(', '), [types]);
  const images = useMemo(() => pictures?.map((picture) => picture.url) || [], [pictures]);

  return (
    <EmployerCard
      href={href}
      onClick={onClick}
      // twStyles={[isHighlighted ? `border-purple-500 shadow-[inset_0_0_0_1px_theme('colors.purple.100')]` : '']}
    >
      <EmployerCardHeader
        badge={openJpsCount ? { text: bagdeText, variant: badgeVariant } : undefined}
        logo={restaurantLogo}
        title={restaurantName}
      />
      <EmployerCardImages images={images} />
      <EmployerCardInfo
        info={{
          address,
          businessName: restaurantTypes,
        }}
      />
      {!!candidates?.candidates && (
        <EmployerCardFooter
          notification={{
            candidateCount: candidates?.count,
            message: candidates?.message,
            candidates: candidates?.candidates,
            isAllSpontaneous: candidates?.isAllSpontaneous,
          }}
        />
      )}
      {isShowcaseOffline && (
        <div className="h-full flex items-end">
          <div className="h-10 border border-neutral-40 rounded-lg flex items-center px-4 gap-2 text-[13px] text-neutral-700 w-full md:!text-sm">
            <EyeSlashIcon className="h-4 w-4 md:h-5 md:w-5 md:min-h-[20px] md:min-w-[20px] text-neutral-700" />
            <span>
              {translateCommon('the_showcase_for_this_venue_is')} <strong>{translateCommon('offline')}</strong>
            </span>
          </div>
        </div>
      )}
    </EmployerCard>
  );
}

export default EmployerRestaurantCard;
